import { createContext, useState, useContext } from 'react';
export const SimulationContext = createContext();

// Create a Provider component
export const SimulationProvider = ({ children }) => {
  const [selectedCharacterId, setSelectedCharacterId] = useState(null);
  const [characterIds, setCharacterIds] = useState([]);
  const [characterGlb, setCharacterGlb] = useState(null);
  const [environmentId, setEnvironmentId] = useState('');
  const [isClientInitialized, setIsClientInitialized] = useState(false);
  const [simulationType, setSimulationType] = useState(null);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [renderer, setRenderer] = useState(null);
  // Engine type of the level's first character, sent only for Basic simulations.
  // Selects the Nudgyt Agent client over Convai when NUDGYT_CORE.
  const [characterEngineType, setCharacterEngineType] = useState(null);
  // Multi-character Core simulation: one shared agent session, one bot per
  // character. botIds[i] belongs to characterIds[i].
  const [isMultiCharacter, setIsMultiCharacter] = useState(false);
  const [botIds, setBotIds] = useState([]);

  // Nudgyt Agent state
  const [nudgytSessionToken, setNudgytSessionToken] = useState(null);
  const [documentIds, setDocumentIds] = useState([]);
  // Stores AI conversation settings including initiation preferences and snooze configuration.
  const [userInteract, setUserInteract] = useState(null);

  // Client state
  const [userText, setUserText] = useState('');
  const [isAiTalking, setIsAiTalking] = useState(false);
  const [facialData, setFacialData] = useState([]);
  const [emotion, setEmotion] = useState({
    emotion: 'neutral',
    intensity: '1'
  });
  const [convaiSessionId, setConvaiSessionId] = useState(null);
  const [previousLevelContext, setPreviousLevelContext] = useState(null);
  const [isEmotionRecognitionEnabled, setIsEmotionRecognitionEnabled] =
    useState(false);

  // Voice mode state (for Basic simulation)
  const [isVoiceModeActive, setIsVoiceModeActive] = useState(false);
  const [isUserInterrupt, setIsUserInterrupt] = useState(false);
  const [isStopResponseRequested, setIsStopResponseRequested] = useState(false);
  const [isDebugVad, setIsDebugVad] = useState(true);

  // Live Chat state (Core only). Classic drives its mic from isVoiceModeActive
  // above; the two pipelines share no state.
  const [isLiveChatActive, setIsLiveChatActive] = useState(false);
  const [isLiveChatInterrupt, setIsLiveChatInterrupt] = useState(false);

  // User emotion detection state
  const [userEmotion, setUserEmotion] = useState(null);
  const [textQueryUserEmotion, setTextQueryUserEmotion] = useState(null);
  const [isEmotionDetectionReady, setIsEmotionDetectionReady] = useState(false);

  // Anam
  const [anamSessionToken, setAnamSessionToken] = useState(null);
  // Avatar visibility (toggled by the parent app). Hiding tears down the Anam
  // session so it stops consuming Anam usage; chat and audio keep working.
  const [isAvatarHidden, setIsAvatarHidden] = useState(false);
  // True while the local MP3 queue is speaking a reply (the fallback path used
  // while the avatar is hidden), so the avatar can wait for it to finish.
  const [isLocalAudioPlaying, setIsLocalAudioPlaying] = useState(false);

  const resetContext = () => {
    setUserText('');
    setNudgytSessionToken(null);
    setFacialData([]);
    setRenderer(null);
    setIsAiTalking(false);
    setDocumentIds([]);
    setCharacterIds([]);
    setCharacterGlb(null);
    setEnvironmentId('');
    setIsDebugVad(false);
    setUserEmotion(null);
    setTextQueryUserEmotion(null);
    setUserInteract(null);
    setIsSceneReady(false);
    setSimulationType(null);
    setCharacterEngineType(null);
    setIsMultiCharacter(false);
    setBotIds([]);
    setConvaiSessionId(null);
    setAnamSessionToken(null);
    setIsAvatarHidden(false);
    setIsLocalAudioPlaying(false);
    setIsUserInterrupt(false);
    setIsStopResponseRequested(false);
    setIsClientInitialized(false);
    setIsVoiceModeActive(false);
    setIsLiveChatActive(false);
    setIsLiveChatInterrupt(false);
    setEmotion({
      emotion: 'neutral',
      intensity: '1'
    });
    setSelectedCharacterId(null);
    setPreviousLevelContext(null);
    setIsEmotionDetectionReady(false);
    setIsEmotionRecognitionEnabled(false);
  };

  return (
    <SimulationContext.Provider
      value={{
        emotion,
        userText,
        renderer,
        isAiTalking,
        facialData,
        isDebugVad,
        documentIds,
        userEmotion,
        textQueryUserEmotion,
        characterIds,
        characterGlb,
        userInteract,
        isSceneReady,
        environmentId,
        simulationType,
        characterEngineType,
        isMultiCharacter,
        botIds,
        isUserInterrupt,
        isStopResponseRequested,
        convaiSessionId,
        anamSessionToken,
        isAvatarHidden,
        isLocalAudioPlaying,
        isClientInitialized,
        isVoiceModeActive,
        isLiveChatActive,
        isLiveChatInterrupt,
        nudgytSessionToken,
        selectedCharacterId,
        previousLevelContext,
        isEmotionDetectionReady,
        isEmotionRecognitionEnabled,
        setEmotion,
        setUserText,
        setRenderer,
        resetContext,
        setIsAiTalking,
        setFacialData,
        setIsDebugVad,
        setDocumentIds,
        setUserEmotion,
        setTextQueryUserEmotion,
        setUserInteract,
        setCharacterIds,
        setCharacterGlb,
        setIsSceneReady,
        setEnvironmentId,
        setSimulationType,
        setCharacterEngineType,
        setIsMultiCharacter,
        setBotIds,
        setConvaiSessionId,
        setIsUserInterrupt,
        setIsStopResponseRequested,
        setAnamSessionToken,
        setIsAvatarHidden,
        setIsLocalAudioPlaying,
        setIsClientInitialized,
        setIsVoiceModeActive,
        setIsLiveChatActive,
        setIsLiveChatInterrupt,
        setNudgytSessionToken,
        setSelectedCharacterId,
        setPreviousLevelContext,
        setIsEmotionDetectionReady,
        setIsEmotionRecognitionEnabled
      }}
    >
      {children}
    </SimulationContext.Provider>
  );
};

// Custom hook to use the ConvaiContext
export const useSimulationContext = () => {
  return useContext(SimulationContext);
};
